import { Schema } from "effect";
import {
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
  UploadIdSchema,
} from "./sandbox";
import { WorkspaceEntryNameSchema } from "./workspace-entries";

/** Single-request uploads stay below the smallest documented Worker request-body ceiling. */
export const PROJECT_FILE_UPLOAD_MAX_BYTES = 90 * 1_024 * 1_024;
export const PROJECT_FILE_CAPABILITY_TTL_SECONDS = 15 * 60;
export const PROJECT_FILE_MESSAGE_MAX_ATTACHMENTS = 4;
export const PROJECT_FILE_MESSAGE_MAX_BYTES = 20 * 1_024 * 1_024;
export const PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES = 40 * 1_024 * 1_024;

export const ProjectFileBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

const CanonicalTextSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      value === value.trim() &&
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "canonical text without control characters" },
  ),
);
const CanonicalIdSchema = CanonicalTextSchema.check(Schema.isMaxLength(160));
const PositiveIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const IsoDateTimeSchema = Schema.String.check(
  Schema.isMaxLength(40),
  Schema.makeFilter(
    (value) =>
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/u.test(value) &&
      Number.isFinite(Date.parse(value)),
    { expected: "an ISO-8601 UTC timestamp" },
  ),
);
const HttpUrlSchema = Schema.String.check(
  Schema.isMaxLength(8_192),
  Schema.makeFilter((value) => /^https?:\/\/[^\s/?#@]+(?:[/?#]|$)/iu.test(value), {
    expected: "a credential-free HTTP URL",
  }),
);
const Sha256Schema = Schema.String.check(
  Schema.makeFilter((value) => /^[a-f0-9]{64}$/u.test(value), {
    expected: "a lowercase SHA-256 digest",
  }),
);
const EtagSchema = CanonicalTextSchema.check(Schema.isMaxLength(512));

export {
  EtagSchema as ProjectFileEtagSchema,
  HttpUrlSchema as ProjectFileHttpUrlSchema,
  IsoDateTimeSchema as ProjectFileIsoDateTimeSchema,
  PositiveIntegerSchema as ProjectFilePositiveIntegerSchema,
  Sha256Schema as ProjectFileSha256Schema,
};

export const ProjectFileNameSchema = WorkspaceEntryNameSchema;
export const ProjectFileContentTypeSchema = Schema.String.check(
  Schema.isMinLength(3),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      (value === value.trim().toLowerCase() &&
        /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/u.test(value)) ||
      "must be a canonical MIME media type",
  ),
);
export const ProjectFilePrivateObjectKeySchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2_048),
  Schema.makeFilter(
    (value) =>
      (!value.startsWith("/") &&
        !value.includes("\\") &&
        value
          .split("/")
          .every((segment) => segment !== "" && segment !== "." && segment !== "..")) ||
      "must be a canonical private object key without traversal segments",
  ),
);

export function projectFileContentTypeCanAttachToModel(contentType: string): boolean {
  return (
    contentType === "application/json" ||
    contentType === "application/pdf" ||
    contentType === "application/xml" ||
    contentType === "application/x-yaml" ||
    contentType === "application/yaml" ||
    contentType.startsWith("text/") ||
    ["image/gif", "image/jpeg", "image/png", "image/webp"].includes(contentType)
  );
}

export const ProjectFileModelContentTypeSchema = ProjectFileContentTypeSchema.check(
  Schema.makeFilter(
    (contentType) =>
      projectFileContentTypeCanAttachToModel(contentType) ||
      "must be a supported PDF, text, data, or image media type",
  ),
);

export const ProjectFileIdSchema = CanonicalIdSchema.pipe(Schema.brand("ProjectFileId"));
export const ProjectFileRevisionIdSchema = CanonicalIdSchema.pipe(
  Schema.brand("ProjectFileRevisionId"),
);
export const ProjectFileStorageObjectIdSchema = CanonicalIdSchema.pipe(
  Schema.brand("ProjectFileStorageObjectId"),
);
export const ProjectFileCleanupJobIdSchema = CanonicalIdSchema.pipe(
  Schema.brand("ProjectFileCleanupJobId"),
);
export const ProjectFileActorUserIdSchema = CanonicalIdSchema.pipe(
  Schema.brand("ProjectFileActorUserId"),
);
export const ProjectFileCapabilityTokenSchema = Schema.String.check(
  Schema.makeFilter((value) => /^[A-Za-z0-9_-]{43}$/u.test(value), {
    expected: "a 256-bit URL-safe capability token",
  }),
).pipe(Schema.brand("ProjectFileCapabilityToken"));

/**
 * Durable AI SDK UI data. The exact authorized bytes are resolved from this reference for one
 * request; neither the bytes nor the private capability URL are added to the persisted message.
 */
export const ProjectFileMessageReferenceSchema = Schema.Struct({
  version: Schema.Literal("project-file-reference/v1"),
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileModelContentTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(PROJECT_FILE_MESSAGE_MAX_BYTES)),
  sha256: Sha256Schema,
});
export const ProjectFileMessageReferencesSchema = Schema.Array(
  ProjectFileMessageReferenceSchema,
).check(
  Schema.isMaxLength(PROJECT_FILE_MESSAGE_MAX_ATTACHMENTS),
  Schema.makeFilter(
    (references) =>
      new Set(references.map((reference) => reference.fileRevisionId)).size === references.length ||
      "must not contain the same file revision more than once",
  ),
  Schema.makeFilter(
    (references) =>
      references.reduce((total, reference) => total + reference.byteSize, 0) <=
        PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES ||
      `must reference at most ${PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES} bytes in total`,
  ),
);
export const ProjectFileMessageReferenceStandardSchema = Schema.toStandardSchemaV1(
  ProjectFileMessageReferenceSchema,
  { parseOptions: ProjectFileBoundaryParseOptions },
);

const CapabilityHeaderValueSchema = Schema.String.check(
  Schema.isMaxLength(4_096),
  Schema.makeFilter(
    (value) => !value.includes("\u0000") && !value.includes("\r") && !value.includes("\n"),
    { expected: "a single-line capability header value" },
  ),
);
export const ProjectFileCapabilityHeadersSchema = Schema.Record(
  Schema.String,
  CapabilityHeaderValueSchema,
).check(
  Schema.makeFilter(
    (headers) => Object.keys(headers).length <= 8 || "must contain at most 8 headers",
  ),
  Schema.makeFilter(
    (headers) =>
      Object.keys(headers).every((name) => /^[a-z0-9-]{1,128}$/u.test(name)) ||
      "header names must be lowercase HTTP tokens",
  ),
  Schema.makeFilter((headers) => {
    const forbidden = Object.keys(headers).find((name) =>
      ["authorization", "cookie", "proxy-authorization", "set-cookie"].includes(name),
    );
    return forbidden === undefined || `forbidden capability header: ${forbidden}`;
  }),
);

export const ProjectFilePublicErrorCodeSchema = Schema.Literals([
  "PROJECT_FILE_INVALID_INPUT",
  "PROJECT_FILE_TOO_LARGE",
  "PROJECT_FILE_FORBIDDEN",
  "PROJECT_FILE_NOT_FOUND",
  "PROJECT_FILE_NOT_READY",
  "PROJECT_FILE_CONFLICT",
  "PROJECT_FILE_UPLOAD_EXPIRED",
  "PROJECT_FILE_UPLOAD_INCOMPLETE",
  "PROJECT_FILE_INTEGRITY_ERROR",
  "PROJECT_FILE_QUOTA_EXCEEDED",
  "PROJECT_FILE_RATE_LIMITED",
  "PROJECT_FILE_CANCELLED",
  "PROJECT_FILE_UNAVAILABLE",
]);
export const ProjectFilePublicErrorSchema = Schema.Struct({
  code: ProjectFilePublicErrorCodeSchema,
  message: CanonicalTextSchema.check(Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});
export const ProjectFileRepositoryErrorDataSchema = Schema.Struct({
  code: Schema.Literals([
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "INVALID_INPUT",
    "REVISION_CONFLICT",
    "UPLOAD_FINALIZE_CONFLICT",
    "UPLOAD_NOT_PENDING",
    "UPLOAD_STATE_CONFLICT",
    "FILE_IN_USE",
    "QUOTA_EXCEEDED",
    "RATE_LIMITED",
  ]),
  message: CanonicalTextSchema.check(Schema.isMaxLength(500)),
});

export const ProjectFileRevisionPreconditionSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("must-not-exist") }),
  Schema.Struct({
    kind: Schema.Literal("match"),
    revisionId: ProjectFileRevisionIdSchema,
  }),
]);

export const CreateProjectFileUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  fileId: Schema.optionalKey(ProjectFileIdSchema),
  precondition: ProjectFileRevisionPreconditionSchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(PROJECT_FILE_UPLOAD_MAX_BYTES)),
  sha256: Sha256Schema,
}).check(
  Schema.makeFilter(
    (request) =>
      (request.fileId === undefined && request.precondition.kind === "must-not-exist") ||
      (request.fileId !== undefined && request.precondition.kind === "match") ||
      "must use must-not-exist for a new file and match for an existing file",
  ),
);

export const ProjectFileUploadSessionSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  revision: PositiveIntegerSchema,
  method: Schema.Literal("PUT"),
  url: HttpUrlSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
  requiredHeaders: ProjectFileCapabilityHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});

export const CompleteProjectFileUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
});
export const ProjectFileUploadResultSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  revision: PositiveIntegerSchema,
  status: Schema.Literal("ready"),
});
export const CancelProjectFileUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
});
export const ProjectFileUploadCancellationSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  revision: PositiveIntegerSchema,
  status: Schema.Literals(["cancelled", "ready"]),
});

export const DeleteProjectFileRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  fileId: ProjectFileIdSchema,
  expectedMetadataRevision: PositiveIntegerSchema,
  confirmation: Schema.Literal("delete-project-file"),
});
export const ProjectFileDeletionResultSchema = Schema.Struct({
  fileId: ProjectFileIdSchema,
  status: Schema.Literals(["cleanup-pending", "deleted"]),
});

export const CreateProjectFileDownloadTicketRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: Schema.optionalKey(ProjectFileRevisionIdSchema),
});
export const ProjectFileDownloadTicketSchema = Schema.Struct({
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  url: HttpUrlSchema,
  expiresAt: IsoDateTimeSchema,
});

export const ResolveProjectFileActorRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  operation: Schema.Literals(["read", "write", "manage"]),
});
export const ResolvedProjectFileActorSchema = Schema.Struct({
  userId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  role: Schema.Literals(["owner", "admin", "developer", "member", "billing", "guest"]),
});

/** Signed Agent API -> Convex repository contracts. */
export const ProjectFileRepositoryPrepareUploadRequestSchema = Schema.Struct({
  actorUserId: ProjectFileActorUserIdSchema,
  request: CreateProjectFileUploadRequestSchema,
  expiresAt: IsoDateTimeSchema,
});
export const ProjectFileRepositoryPrepareUploadResultSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  storageObjectId: ProjectFileStorageObjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  revision: PositiveIntegerSchema,
  expiresAt: IsoDateTimeSchema,
});
export const ProjectFileRepositoryResolveUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
});
export const ProjectFileRepositoryResolvedUploadSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  storageObjectId: ProjectFileStorageObjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  revision: PositiveIntegerSchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  status: Schema.Literals([
    "pending",
    "finalized",
    "quarantined",
    "conflicted",
    "expired",
    "cancelled",
  ]),
  expiresAt: IsoDateTimeSchema,
});
export const ProjectFileRepositoryFinalizeUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  etag: EtagSchema,
});
export const ProjectFileRepositoryFinalizeUploadResultSchema = Schema.Struct({
  status: Schema.Literals(["ready", "expired", "quarantined", "conflicted"]),
});
export const ProjectFileRepositoryCancelUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
});
export const ProjectFileRepositoryCancelUploadResultSchema = ProjectFileUploadCancellationSchema;
export const ProjectFileRepositoryDeleteRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  fileId: ProjectFileIdSchema,
  expectedMetadataRevision: PositiveIntegerSchema,
  confirmation: Schema.Literal("delete-project-file"),
});
export const ProjectFileRepositoryDeleteResultSchema = ProjectFileDeletionResultSchema;
export const ProjectFileRepositoryResolveDownloadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: Schema.optionalKey(ProjectFileRevisionIdSchema),
});
export const ProjectFileRepositoryResolvedDownloadSchema = Schema.Struct({
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  storageObjectId: ProjectFileStorageObjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
});
export const ClaimProjectFileCleanupRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  owner: CanonicalIdSchema,
  limit: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 20 })),
});
export const ProjectFileCleanupClaimSchema = Schema.Struct({
  cleanupJobId: ProjectFileCleanupJobIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  uploadId: UploadIdSchema,
  storageObjectId: ProjectFileStorageObjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  attempt: PositiveIntegerSchema,
  leaseExpiresAt: IsoDateTimeSchema,
});
export const ProjectFileCleanupClaimsSchema = Schema.Array(ProjectFileCleanupClaimSchema).check(
  Schema.isMaxLength(20),
);
export const ProjectFileCleanupErrorCodeSchema = Schema.Literals([
  "WORKER_AUTHENTICATION_FAILED",
  "WORKER_INTEGRITY_ERROR",
  "WORKER_INVALID_RESPONSE",
  "WORKER_REQUEST_CANCELLED",
  "WORKER_UNAVAILABLE",
]);
export const RecordProjectFileCleanupResultRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  cleanupJobId: ProjectFileCleanupJobIdSchema,
  owner: CanonicalIdSchema,
  attempt: PositiveIntegerSchema,
  outcome: Schema.Literals(["confirmed", "retry"]),
  errorCode: Schema.optionalKey(ProjectFileCleanupErrorCodeSchema),
});
export const ProjectFileCleanupResultSchema = Schema.Struct({
  cleanupJobId: ProjectFileCleanupJobIdSchema,
  status: Schema.Literals(["pending", "completed", "abandoned"]),
  attempts: PositiveIntegerSchema,
});

/** Signed Agent API -> Worker contracts. */
export const ProbeProjectFileRuntimeRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
});
export const ProjectFileRuntimeProbeSchema = Schema.Struct({
  version: Schema.Literal("project-file-runtime-probe/v1"),
});
export const IssueProjectFileUploadCapabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  uploadId: UploadIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(PROJECT_FILE_UPLOAD_MAX_BYTES)),
  sha256: Sha256Schema,
  expiresAt: IsoDateTimeSchema,
});
export const ProjectFileUploadCapabilitySchema = Schema.Struct({
  url: HttpUrlSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
  requiredHeaders: ProjectFileCapabilityHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});
export const CompleteProjectFilePutRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
});
export const RevokeProjectFileUploadCapabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  completionToken: ProjectFileCapabilityTokenSchema,
  uploadId: UploadIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
});
export const RevokeProjectFileUploadCapabilityResultSchema = Schema.Struct({
  status: Schema.Literal("revoked"),
});
export const ProjectFileCompletedObjectSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  etag: EtagSchema,
});
export const IssueProjectFileDownloadCapabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  responseContentType: Schema.optionalKey(ProjectFileContentTypeSchema),
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
});
export const ProjectFileDownloadCapabilitySchema = Schema.Struct({
  url: HttpUrlSchema,
  expiresAt: IsoDateTimeSchema,
});
export const DeleteProjectFileObjectRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  uploadId: UploadIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
});
export const DeleteProjectFileObjectResultSchema = Schema.Struct({
  status: Schema.Literals(["deleted", "absent"]),
});

export type ProjectFilePublicErrorCode = typeof ProjectFilePublicErrorCodeSchema.Type;
export type ProjectFileMessageReference = typeof ProjectFileMessageReferenceSchema.Type;
export type CreateProjectFileUploadRequest = typeof CreateProjectFileUploadRequestSchema.Type;
export type ProjectFileRevisionPrecondition = typeof ProjectFileRevisionPreconditionSchema.Type;
export type ProjectFileUploadSession = typeof ProjectFileUploadSessionSchema.Type;
export type CompleteProjectFileUploadRequest = typeof CompleteProjectFileUploadRequestSchema.Type;
export type ProjectFileUploadResult = typeof ProjectFileUploadResultSchema.Type;
export type CancelProjectFileUploadRequest = typeof CancelProjectFileUploadRequestSchema.Type;
export type ProjectFileUploadCancellation = typeof ProjectFileUploadCancellationSchema.Type;
export type DeleteProjectFileRequest = typeof DeleteProjectFileRequestSchema.Type;
export type ProjectFileDeletionResult = typeof ProjectFileDeletionResultSchema.Type;
export type CreateProjectFileDownloadTicketRequest =
  typeof CreateProjectFileDownloadTicketRequestSchema.Type;
export type ProjectFileDownloadTicket = typeof ProjectFileDownloadTicketSchema.Type;
export type ResolveProjectFileActorRequest = typeof ResolveProjectFileActorRequestSchema.Type;
export type ResolvedProjectFileActor = typeof ResolvedProjectFileActorSchema.Type;
export type ProjectFileRepositoryPrepareUploadRequest =
  typeof ProjectFileRepositoryPrepareUploadRequestSchema.Type;
export type ProjectFileRepositoryPrepareUploadResult =
  typeof ProjectFileRepositoryPrepareUploadResultSchema.Type;
export type ProjectFileRepositoryResolveUploadRequest =
  typeof ProjectFileRepositoryResolveUploadRequestSchema.Type;
export type ProjectFileRepositoryResolvedUpload =
  typeof ProjectFileRepositoryResolvedUploadSchema.Type;
export type ProjectFileRepositoryFinalizeUploadRequest =
  typeof ProjectFileRepositoryFinalizeUploadRequestSchema.Type;
export type ProjectFileRepositoryFinalizeUploadResult =
  typeof ProjectFileRepositoryFinalizeUploadResultSchema.Type;
export type ProjectFileRepositoryCancelUploadRequest =
  typeof ProjectFileRepositoryCancelUploadRequestSchema.Type;
export type ProjectFileRepositoryCancelUploadResult =
  typeof ProjectFileRepositoryCancelUploadResultSchema.Type;
export type ProjectFileRepositoryDeleteRequest =
  typeof ProjectFileRepositoryDeleteRequestSchema.Type;
export type ProjectFileRepositoryDeleteResult = typeof ProjectFileRepositoryDeleteResultSchema.Type;
export type ProjectFileRepositoryResolveDownloadRequest =
  typeof ProjectFileRepositoryResolveDownloadRequestSchema.Type;
export type ProjectFileRepositoryResolvedDownload =
  typeof ProjectFileRepositoryResolvedDownloadSchema.Type;
export type ClaimProjectFileCleanupRequest = typeof ClaimProjectFileCleanupRequestSchema.Type;
export type ProjectFileCleanupClaim = typeof ProjectFileCleanupClaimSchema.Type;
export type ProjectFileCleanupErrorCode = typeof ProjectFileCleanupErrorCodeSchema.Type;
export type RecordProjectFileCleanupResultRequest =
  typeof RecordProjectFileCleanupResultRequestSchema.Type;
export type ProjectFileCleanupResult = typeof ProjectFileCleanupResultSchema.Type;
export type ProbeProjectFileRuntimeRequest = typeof ProbeProjectFileRuntimeRequestSchema.Type;
export type ProjectFileRuntimeProbe = typeof ProjectFileRuntimeProbeSchema.Type;
export type IssueProjectFileUploadCapabilityRequest =
  typeof IssueProjectFileUploadCapabilityRequestSchema.Type;
export type ProjectFileUploadCapability = typeof ProjectFileUploadCapabilitySchema.Type;
export type CompleteProjectFilePutRequest = typeof CompleteProjectFilePutRequestSchema.Type;
export type RevokeProjectFileUploadCapabilityRequest =
  typeof RevokeProjectFileUploadCapabilityRequestSchema.Type;
export type RevokeProjectFileUploadCapabilityResult =
  typeof RevokeProjectFileUploadCapabilityResultSchema.Type;
export type ProjectFileCompletedObject = typeof ProjectFileCompletedObjectSchema.Type;
export type IssueProjectFileDownloadCapabilityRequest =
  typeof IssueProjectFileDownloadCapabilityRequestSchema.Type;
export type ProjectFileDownloadCapability = typeof ProjectFileDownloadCapabilitySchema.Type;
export type DeleteProjectFileObjectRequest = typeof DeleteProjectFileObjectRequestSchema.Type;
export type DeleteProjectFileObjectResult = typeof DeleteProjectFileObjectResultSchema.Type;

export const decodeCreateProjectFileUploadRequest = Schema.decodeUnknownSync(
  CreateProjectFileUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileMessageReference = Schema.decodeUnknownSync(
  ProjectFileMessageReferenceSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileMessageReferences = Schema.decodeUnknownSync(
  ProjectFileMessageReferencesSchema,
  ProjectFileBoundaryParseOptions,
);
export const encodeProjectFileMessageReference = Schema.encodeSync(
  ProjectFileMessageReferenceSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileUploadSession = Schema.decodeUnknownSync(
  ProjectFileUploadSessionSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeCompleteProjectFileUploadRequest = Schema.decodeUnknownSync(
  CompleteProjectFileUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileUploadResult = Schema.decodeUnknownSync(
  ProjectFileUploadResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeCancelProjectFileUploadRequest = Schema.decodeUnknownSync(
  CancelProjectFileUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileUploadCancellation = Schema.decodeUnknownSync(
  ProjectFileUploadCancellationSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeDeleteProjectFileRequest = Schema.decodeUnknownSync(
  DeleteProjectFileRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileDeletionResult = Schema.decodeUnknownSync(
  ProjectFileDeletionResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeCreateProjectFileDownloadTicketRequest = Schema.decodeUnknownSync(
  CreateProjectFileDownloadTicketRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileDownloadTicket = Schema.decodeUnknownSync(
  ProjectFileDownloadTicketSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFilePublicError = Schema.decodeUnknownSync(
  ProjectFilePublicErrorSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryErrorData = Schema.decodeUnknownSync(
  ProjectFileRepositoryErrorDataSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeResolveProjectFileActorRequest = Schema.decodeUnknownSync(
  ResolveProjectFileActorRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeResolvedProjectFileActor = Schema.decodeUnknownSync(
  ResolvedProjectFileActorSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryPrepareUploadRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryPrepareUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryPrepareUploadResult = Schema.decodeUnknownSync(
  ProjectFileRepositoryPrepareUploadResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryResolveUploadRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryResolveUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryResolvedUpload = Schema.decodeUnknownSync(
  ProjectFileRepositoryResolvedUploadSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryFinalizeUploadRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryFinalizeUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryFinalizeUploadResult = Schema.decodeUnknownSync(
  ProjectFileRepositoryFinalizeUploadResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryCancelUploadRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryCancelUploadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryCancelUploadResult = Schema.decodeUnknownSync(
  ProjectFileRepositoryCancelUploadResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryDeleteRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryDeleteRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryDeleteResult = Schema.decodeUnknownSync(
  ProjectFileRepositoryDeleteResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryResolveDownloadRequest = Schema.decodeUnknownSync(
  ProjectFileRepositoryResolveDownloadRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRepositoryResolvedDownload = Schema.decodeUnknownSync(
  ProjectFileRepositoryResolvedDownloadSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeClaimProjectFileCleanupRequest = Schema.decodeUnknownSync(
  ClaimProjectFileCleanupRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileCleanupClaims = Schema.decodeUnknownSync(
  ProjectFileCleanupClaimsSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeRecordProjectFileCleanupResultRequest = Schema.decodeUnknownSync(
  RecordProjectFileCleanupResultRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileCleanupResult = Schema.decodeUnknownSync(
  ProjectFileCleanupResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProbeProjectFileRuntimeRequest = Schema.decodeUnknownSync(
  ProbeProjectFileRuntimeRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileRuntimeProbe = Schema.decodeUnknownSync(
  ProjectFileRuntimeProbeSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeIssueProjectFileUploadCapabilityRequest = Schema.decodeUnknownSync(
  IssueProjectFileUploadCapabilityRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileUploadCapability = Schema.decodeUnknownSync(
  ProjectFileUploadCapabilitySchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeCompleteProjectFilePutRequest = Schema.decodeUnknownSync(
  CompleteProjectFilePutRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeRevokeProjectFileUploadCapabilityRequest = Schema.decodeUnknownSync(
  RevokeProjectFileUploadCapabilityRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeRevokeProjectFileUploadCapabilityResult = Schema.decodeUnknownSync(
  RevokeProjectFileUploadCapabilityResultSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileCompletedObject = Schema.decodeUnknownSync(
  ProjectFileCompletedObjectSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeIssueProjectFileDownloadCapabilityRequest = Schema.decodeUnknownSync(
  IssueProjectFileDownloadCapabilityRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeProjectFileDownloadCapability = Schema.decodeUnknownSync(
  ProjectFileDownloadCapabilitySchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeDeleteProjectFileObjectRequest = Schema.decodeUnknownSync(
  DeleteProjectFileObjectRequestSchema,
  ProjectFileBoundaryParseOptions,
);
export const decodeDeleteProjectFileObjectResult = Schema.decodeUnknownSync(
  DeleteProjectFileObjectResultSchema,
  ProjectFileBoundaryParseOptions,
);
